import { useState } from 'react';
import type { ResourceOccupancyEntry } from '@/shared/api';
import { insideWindow } from './resourceBlockModel';
import { initialDay, MAX_OCCUPANCY_DAYS } from './resourceCalendarModel';
import type { GridSelection } from './resourceGridModel';
import { useResourceMutations, useResourceOccupancy } from './useResourceOccupancy';

interface Options {
  groupId: string;
  dates: string[];
  today: string;
  /** Der Tag aus der Adresse, wenn die Seite ihn führt; sonst merkt sich die Karte ihn selbst. */
  day?: string;
  onDay?: (day: string) => void;
}

/**
 * Zustand der Ressourcenkarte: Tag, Markierung, Sperren-Blatt und Aufheben-Dialog. Eine Markierung
 * gilt nur an ihrem Tag und nur, solange ihre Ressource aktiv ist und das Fenster sie fasst – nach
 * einem Neuladen springt „Zeit sperren“ nie still auf eine andere Ressource oder Zeit.
 */
export function useResourceCalendar({ groupId, dates, today, day: wanted, onDay }: Options) {
  const days = dates.slice(0, MAX_OCCUPANCY_DAYS);
  const [ownDay, setOwnDay] = useState(() => initialDay(days, today));
  const requested = wanted ?? ownDay;
  const day = days.includes(requested) ? requested : initialDay(days, today);
  const occupancy = useResourceOccupancy(groupId, days[0] ?? '', days.at(-1) ?? '');
  const [picked, setPicked] = useState<{ date: string; selection: GridSelection } | null>(null);
  const [blocking, setBlocking] = useState<{ resourceId: string; start?: number; end?: number }>();
  const [cancelling, setCancelling] = useState<ResourceOccupancyEntry | null>(null);
  const mutations = useResourceMutations(groupId, () => {
    setBlocking(undefined);
    setPicked(null);
  });
  const active = occupancy.data?.resources.filter((row) => row.isActive) ?? [];
  const owner = active.find((row) => row.resourceId === picked?.selection.resourceId);
  const selection =
    picked?.date === day &&
    owner &&
    insideWindow(picked.selection.startMinute, picked.selection.endMinute, owner)
      ? picked.selection
      : null;

  return {
    days,
    day,
    occupancy,
    active,
    selection,
    selectionName: owner?.name,
    blocking,
    cancelling,
    ...mutations,
    chooseDay: (next: string) => {
      mutations.clearError();
      setPicked(null);
      if (onDay) onDay(next);
      else setOwnDay(next);
    },
    select: (next: GridSelection) => setPicked({ date: day, selection: next }),
    clearSelection: () => setPicked(null),
    openBlock: (resourceId: string, start?: number, end?: number) => {
      mutations.clearError();
      setBlocking({ resourceId, start, end });
    },
    closeBlock: () => {
      mutations.clearError();
      setBlocking(undefined);
    },
    askCancel: (entry: ResourceOccupancyEntry) => {
      mutations.clearError();
      setCancelling(entry);
    },
    closeCancel: () => {
      mutations.clearError();
      setCancelling(null);
    },
    // Offen bleibt der Dialog bei einer Ablehnung: dort steht der Grund.
    confirmCancel: (confirmPaid: boolean) => {
      if (!cancelling) return;
      mutations.cancel.mutate(
        { id: cancelling.id, confirmPaid },
        { onSuccess: () => setCancelling(null) },
      );
    },
  };
}
